import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import { AudioDevice } from './audio-device.types';
import { mapInputDevice, mapOutputDevice, resolvePermissionError } from './audio-device.util';

export function useAudioDevices() {
  const { t } = useTranslation();
  const [audioInputs, setAudioInputs] = useState<AudioDevice[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<AudioDevice[]>([]);
  const [selectedInput, setSelectedInput] = useState('');
  const [selectedOutput, setSelectedOutput] = useState('');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isInIframe = window.self !== window.top;

  const loadDevices = useCallback(async () => {
    try {
      setIsLoading(true);
      setPermissionError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t('settings:mic.mediaDevicesNotAvailable'));
      }

      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();

      const inputs = devices.filter((d) => d.kind === 'audioinput').map(mapInputDevice);
      const outputs = devices.filter((d) => d.kind === 'audiooutput').map(mapOutputDevice);

      setAudioInputs(inputs);
      setAudioOutputs(outputs);

      const savedInput = localStorage.getItem('preferredAudioInput');
      const savedOutput = localStorage.getItem('preferredAudioOutput');

      if (savedInput && inputs.some((d) => d.deviceId === savedInput)) {
        setSelectedInput(savedInput);
      } else if (inputs.length > 0) {
        setSelectedInput(inputs[0].deviceId);
      }

      if (savedOutput && outputs.some((d) => d.deviceId === savedOutput)) {
        setSelectedOutput(savedOutput);
      } else if (outputs.length > 0) {
        setSelectedOutput(outputs[0].deviceId);
      }
    } catch (error) {
      logger.error('[MicSettings] Error loading devices:', error);
      setPermissionError(resolvePermissionError(error, isInIframe, t));
    } finally {
      setIsLoading(false);
    }
  }, [isInIframe, t]);

  const saveDevicePreferences = useCallback(() => {
    localStorage.setItem('preferredAudioInput', selectedInput);
    localStorage.setItem('preferredAudioOutput', selectedOutput);
  }, [selectedInput, selectedOutput]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    const handleDeviceChange = () => {
      loadDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [loadDevices]);

  return {
    audioInputs,
    audioOutputs,
    selectedInput,
    setSelectedInput,
    selectedOutput,
    setSelectedOutput,
    permissionError,
    isLoading,
    loadDevices,
    saveDevicePreferences,
  };
}

export type { AudioDevice } from './audio-device.types';
